<template>
  <b-row>
    <b-col class="col-12 col-lg-8 mx-auto">
      <div v-if="!isTelegramWebApp" class="px-2 pt-2">
        <b-button variant="link" size="sm" class="pl-0" @click="$router.push('/')">
          <i class="fas fa-arrow-left mr-1"></i>
          На главную
        </b-button>
      </div>
      <b-overlay :show="onProcess" no-center>
        <template #overlay>
          <div class="upload-overlay-warning text-center py-5">
            <b-spinner variant="dark" class="mb-3"></b-spinner>
            <p
              v-if="uploadProgress.total === 0"
              class="text-muted mt-2 mb-0"
            >
              Сохранение отчёта...
            </p>
            <div
              v-else
              class="upload-overlay-warning__text-block"
            > 
              <p class="upload-overlay-warning__text mb-0">
                Не блокируйте экран. Не обновляйте и не сворачивайте страницу.
              </p>
            </div>
          </div>
        </template>
        <horizontal-stepper
          ref="stepper"
          :steps="filteredSteps"
          @completed-step="completeStep"
          @active-step="isStepActive"
          @stepper-finished="onStepperFinished"
          :keep-alive="false"
          locale="ru"
        >
        </horizontal-stepper>
      </b-overlay>
      <div v-if="submissionSettingsError" class="alert alert-danger mt-2" role="alert">
        {{ submissionSettingsError }}
        <b-button size="sm" variant="outline-danger" @click="reloadSubmissionSettings">Повторить загрузку правил</b-button>
      </div>
      <div v-if="submissionError" class="alert alert-danger mt-2" role="alert">
        <strong>{{ submissionError }}</strong>
        <div v-for="(issue, index) in submissionIssues" :key="index" class="mt-2">
          {{ issue.message }} {{ issue.help }}
          <b-button size="sm" variant="outline-danger" @click="openExplanationStep(issue.kind)">{{ issue.kind === 'delay' ? 'Исправить причину просрочки' : 'Открыть фотографии' }}</b-button>
        </div>
      </div>
      
    </b-col>
    
    <!-- Модальное окно подтверждения отчета -->
    <b-modal 
      id="confirmation-modal" 
      title="Подтверждение отчета о смене"      
      centered
      scrollable
      header-bg-variant="primary"
      header-text-variant="white"
      body-class="p-3"
      footer-class="d-flex justify-content-between"
      @hidden="confirmationResolve = null"
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
    >
      <div class="confirmation-report" v-if="modalReportData">
        <div class="mb-4">
          <h5 class="text-primary mb-3">📋 Сводка отчета о смене</h5>
          <p class="text-muted">Пожалуйста, проверьте данные перед отправкой. Если что-то неверно, нажмите "Редактировать".</p>
        </div>
        
        <div class="report-section mb-3">
          <h6 class="text-secondary">🏗️ Объект</h6>
          <p class="mb-1">
            <strong>{{ modalSelectedObject ? (modalSelectedObject.bo && modalSelectedObject.bo.name) || 'Удаленный объект' : 'Не выбран' }}</strong>
          </p>
        </div>
        
        <div class="report-section mb-3">
          <h6 class="text-secondary">⏰ Время работы</h6>
          <div>
              <small class="text-muted">Начало:</small><br>
              <strong>{{ $moment(modalReport.startDate).format('DD.MM.YYYY') }} {{ modalReport.startTime }}</strong>
            </div>
            <div>
              <small class="text-muted">Окончание:</small><br>
              <strong>{{ $moment(modalReport.endDate).format('DD.MM.YYYY') }} {{ modalReport.endTime }}</strong>
            </div>
          <div class="row">            
            <div class="col-12 mt-2" v-if="modalShiftDuration">
              <small class="text-muted">Продолжительность смены:</small><br>
              <strong class="text-info">{{ modalShiftDuration }}</strong>
            </div>
          </div>
        </div>
        
        <div class="report-section mb-3">
          <h6 class="text-secondary">👷 Персонал</h6>
          <div>
              <small class="text-muted">Количество рабочих:</small><br>
              <strong>{{ modalReport.peopleCount || 0 }} чел.</strong>
            </div>
            <div>
              <small class="text-muted">Обеденный перерыв:</small><br>
              <strong>{{ modalReport.pauseTime || 0 }} мин.</strong>
            </div>
        </div>
        
        <!-- Выполненные работы -->
        <div class="report-section mb-3" v-if="modalReport.selectedWorks && modalReport.selectedWorks.length > 0">
          <h6 class="text-secondary">🔨 Выполненные работы</h6>
          <div class="works-list">
            <div 
              v-for="work in modalReport.selectedWorks" 
              :key="work.work_id"
              class="work-item border-left border-primary pl-2 mb-2"
            >
              <div><strong>{{ work.name }}</strong></div>
              <small class="text-muted">Выполнено: {{ work.completed || 0 }} {{ work.unitOfMeasurement || '' }}</small>
              <div v-if="work.peopleCount" class="mt-1">
                <small class="text-info">👷 Рабочих: {{ work.peopleCount }} чел.</small>
              </div>
              
              <!-- Материалы для этой работы -->
              <div v-if="work.materials && work.materials.length > 0" class="mt-2">
                <div class="materials-header">
                  <small class="text-secondary font-weight-bold">📦 Материалы:</small>
                </div>
                <div class="materials-list ml-2">
                  <div 
                    v-for="material in work.materials.filter(m => m.used > 0)" 
                    :key="material.id"
                    class="material-item d-flex align-items-center py-1"
                  >
                    <span class="material-name text-sm">{{ material.name }}:</span>
                    <strong class="material-amount text-success ml-2">{{ material.used }} {{ material.unitOfMeasurement }}</strong>
                  </div>
                  <div v-if="!work.materials.some(m => m.used > 0)" class="text-muted">
                    <small>Материалы не использовались</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Вторичные работы -->
        <div class="report-section mb-3" v-if="modalReport.secondWorks">
          <h6 class="text-secondary">🥈 Сопутствующие работы</h6>
          <p class="small border-left border-info pl-2" v-html="modalReport.secondWorks.replace(/\n/g, '<br>')"></p>
        </div>
        
        <!-- Предложения -->
        <div class="report-section mb-3" v-if="modalReport.notes">
          <h6 class="text-secondary">💡 Предложения/Замечания</h6>
          <p class="small border-left border-warning pl-2" v-html="modalReport.notes.replace(/\n/g, '<br>')"></p>
        </div>
        
        <!-- Топливо -->
        <div class="report-section mb-3" v-if="modalUsedFuel.length > 0">
          <h6 class="text-secondary">⛽ Топливо</h6>
          <div class="fuel-list">
            <div 
              v-for="fuel in modalUsedFuel" 
              :key="fuel.id"
              class="fuel-item border-left border-success pl-2 mb-1"
            >
             <div>
               <span>{{ fuel.name }}:</span>
                <strong class="ml-2">{{ fuel.value }} {{ fuel.unit }}</strong>
             </div>
              <p class="small border-left border-warning pl-2" v-html="fuel.note.replace(/\n/g, '<br>')"></p>             
            </div>           
          </div>
        </div>
        
        <!-- Причина просрочки -->
        <div class="report-section mb-3" v-if="modalReport.reasonForDelay">
          <h6 class="text-secondary text-danger">⚠️ Причина просрочки</h6>
          <blockquote class="user-quote" style="white-space: pre-wrap">{{ explanationPreview('delay') }}</blockquote>
        </div>
        <div class="report-section mb-3" v-if="modalReport.emptyPhotoComment && !readyCount">
          <h6 class="text-secondary text-danger">Причина отсутствия фото</h6>
          <blockquote class="user-quote" style="white-space: pre-wrap">{{ explanationPreview('photo') }}</blockquote>
        </div>
        <div class="report-section mb-3" v-if="readyCount">
          <h6>Фотографии: {{ readyCount }}</h6>
          <ReusableReportPhotos selected-only />
          <small v-if="$store.state.shiftReport.photos.length">Новых файлов с устройства: {{ $store.state.shiftReport.photos.length }}</small>
        </div>
      </div>
      
      <template #modal-footer="{ cancel }">
        <div v-if="submissionError" class="alert alert-danger w-100 mb-2" role="alert">
          <strong>{{ submissionError }}</strong>
          <div v-for="(issue, index) in submissionIssues" :key="index" class="mt-2">
            {{ issue.message }} {{ issue.help }}
          </div>
          <div class="mt-1">Данные сохранены. Можно повторить отправку или вернуться к редактированию.</div>
        </div>
        <b-button 
          variant="secondary" 
          size="lg"
          :disabled="onProcess"
          @click="onEditReport"
        >
          Редактировать
        </b-button>
        <b-button 
          variant="success" 
          size="lg"
          :disabled="onProcess"
          @click="onConfirmReport"
        >
          {{ onProcess ? 'Отправляем…' : submissionError ? 'Повторить отправку' : 'Отправить отчёт' }}
        </b-button>
      </template>
    </b-modal>
  </b-row>
</template>

<script>
import explanations from '../../../../shared/reportExplanations';
import HorizontalStepper from 'vue-stepper';
import StepOne from "./Steps/StepOne";
import StepTwo from "./Steps/StepTwo";
import StepThree from "./Steps/StepThree";
import StepFour from "./Steps/StepFour";
import StepFive from "./Steps/StepFive";
import StepSix from "./Steps/StepSix";
import StepSeven from "./Steps/StepSeven";
import StepEight from "./Steps/StepEight";
import ReusableReportPhotos from './ReusableReportPhotos.vue';
import { 
  closeTelegramWebApp 
} from '@/utils/telegramWebApp';
import {
  awaitInstallPromotionAfterSuccess,
  notifyInstallPromotionSuccess
} from '@/utils/installPromotion';


export default {
  name: "ShiftReport",
  components: {
    ReusableReportPhotos,
    HorizontalStepper    
  },
  computed: {
    submissionSettingsError() { return this.$store.state.shiftReport.submissionSettingsError; },
    /**
     * Получает кэш данных отчета о смене из store
     * @returns {Object} Кэшированные данные отчета
     */
    cache() {
      return this.$store.state.shiftReport.cache;
    },
    
    /**
     * Проверяет, выполняется ли процесс отправки отчета
     * @returns {boolean} Статус процесса
     */
    onProcess() {
      return this.$store.state.shiftReport.onProcess;
    },
    
    /**
     * Проверяет, запущено ли приложение в Telegram WebApp
     * @returns {boolean} Статус Telegram WebApp
     */
    isTelegramWebApp() {
      return this.$store.getters.isTelegramWebApps;
    },
        
    /**
     * Определяет шаги для создания отчета о смене
     * @returns {Array} Массив шагов с конфигурацией
     */
    filteredSteps() {
      return this.steps;
    },
    
    /**
     * Получает данные отчета для отображения в модальном окне
     */
    modalReport() {
      return this.modalReportData && this.modalReportData.report || {};
    },
    
    /**
     * Получает продолжительность смены в читаемом формате
     */
    modalShiftDuration() {
      if (!this.modalReport.startDate || !this.modalReport.startTime || !this.modalReport.endDate || !this.modalReport.endTime) {
        return '';
      }
      
      const startDateTime = this.$moment(`${this.modalReport.startDate} ${this.modalReport.startTime}`, 'YYYY-MM-DD HH:mm');
      const endDateTime = this.$moment(`${this.modalReport.endDate} ${this.modalReport.endTime}`, 'YYYY-MM-DD HH:mm');
      const duration = this.$moment.duration(endDateTime.diff(startDateTime));
      return `${Math.floor(duration.asHours())}ч ${duration.minutes()}мин`;
    },
    
    /**
     * Получает используемое топливо с положительными значениями
     */
    modalUsedFuel() {
      const list = this.modalReport.fuel || [];
      return list.map(f => ({
        ...f,
        value: (f.value === '' || f.value == null) ? 0 : Number(f.value),
        note: f.note || ''
      }));
    },
    uploadMethod() {
      return this.$store.state.shiftReport.uploadMethod;
    },
    uploadProgress() {
      return this.$store.state.shiftReport.uploadProgress;
    },
    photosProcessingStatus() {
      return this.$store.state.shiftReport.photosProcessingStatus;
    },
    readyCount() {
      const photos = this.$store.state.shiftReport.photos;
      return (Array.isArray(photos) ? photos.length : 0) + (this.$store.state.shiftReport.reusedPhotos || []).length;
    },
    photosUploadStatus() {
      return this.$store.state.shiftReport.photosUploadStatus;
    },
    photosUploadErrorMessage() {
      return this.$store.state.shiftReport.photosUploadErrorMessage;
    }
  },
  data() {
    return {
        steps: [
        {
          icon: 'work',
          name: 'sh_first',
          title: 'Объект',
          subtitle: ``,
          component: StepOne,
          completed: false
        },
        {
          icon: 'alarm',
          name: 'sh_time',
          title: 'Время',
          subtitle: ``,
          component: StepTwo,
          completed: true
        },
        {
          icon: 'people',
          name: 'sh_people',
          title: 'Рабочие',
          subtitle: ``,
          component: StepThree,
          completed: false
        },
        {
          icon: 'work',
          name: 'sh_works',
          title: 'Работы',
          subtitle: ``,
          component: StepFour,
          completed: false
        },
        {
          icon: 'receipt',
          name: 'sh_second_works',
          title: 'Вторичные работы',
          subtitle: ``,
          component: StepFive,
          completed: false
        },
        {
          icon: 'speaker_notes',
          name: 'sh_works',
          title: 'Предложения',
          subtitle: ``,
          component: StepSix,
          completed: false
        },
        {
          icon: 'speaker_notes',
          name: 'sh_fuels',
          title: 'Топливо',
          subtitle: ``,
          component: StepSeven,
          completed: false
        },
        {
          icon: 'photo_camera',
          name: 'sh_photos',
          title: 'Фотографии',
          subtitle: ``,
          component: StepEight,
          completed: false
        }              
      ],
      reportCompleted: false,
      submissionError: '',
      submissionIssues: [],
      modalReportData: null,
      modalSelectedObject: null,
      confirmationResolve: null,
      wakeLock: null,
      stopWatchProgress: null,
      resumeHandled: false
    }
  },

  beforeDestroy() {
    if (this.stopWatchProgress) {
      this.stopWatchProgress();
      this.stopWatchProgress = null;
    }
    this.releaseWakeLock();
  },

  mounted() {
    const { $store } = this;
    
    // Инициализация отчета о смене
    $store.dispatch(`shiftReport_init`);

    // Главная нового интерфейса может открыть сохранённый черновик.
    // Предварительный выбор объекта и даты приводит пользователя к тому же
    // cache-flow, который уже используется на шаге времени.
    const processId = Number(this.$route.query.process_id);
    if (Number.isInteger(processId) && processId > 0) {
      $store.commit(`shiftReport_setSelectedProcess`, processId);
    }
    if (
      typeof this.$route.query.start_date === 'string'
      && /^\d{4}-\d{2}-\d{2}$/.test(this.$route.query.start_date)
    ) {
      $store.commit(`shiftReport_setStartDate`, this.$route.query.start_date);
    }
    if (
      String(this.$route.query.resume || '').toLowerCase() === 'true'
      && Number.isInteger(processId)
      && processId > 0
    ) {
      this.$nextTick(() => this.resumeDraft());
    }

  },
  
  methods: {
    async reloadSubmissionSettings() { try { await this.$store.dispatch('shiftReport_loadSubmissionSettings'); } catch (_) { /* Inline feedback remains visible. */ } },
    explanationPreview(kind) {
      const report = this.modalReport;
      const key = kind === 'delay' ? report.delayReasonKey : report.photoReasonKey;
      const detail = kind === 'delay' ? report.reasonForDelay : report.emptyPhotoComment;
      return this.$store.state.shiftReport.textQualityEnabled ? explanations.formatExplanation(kind, key, detail || '') : detail;
    },
    openExplanationStep(kind) {
      const stepper = this.$refs.stepper;
      if (stepper && typeof stepper.activateStep === 'function') stepper.activateStep(kind === 'delay' ? 1 : 7);
    },
    /**
     * Отмечает шаг как завершенный
     * @param {Object} payload - Данные о завершенном шаге
     * @param {string} payload.name - Название шага
     */
    completeStep(payload) {
      this.steps.forEach((step) => {
        if (step.name === payload.name) {
          step.completed = true;
        }
      });
    },
    
    /**
     * Обрабатывает активацию шага и выполняет необходимые действия
     * @param {Object} stepData - Данные о шаге
     * @param {string} stepData.name - Название шага
     */
    async isStepActive({ name }) {
      // Загружаем цели при активации шага работ
      if (name === `sh_works`) {
        this.$store.dispatch(`shiftReport_getTargets`);
      }
      
      // Загружаем доступный кэш при активации шага времени
      if (name === `sh_time`) {
        if (this.resumeHandled) {
          return;
        }
        await this.$store.dispatch(`shiftReport_getAvailableCache`);
      }
    },

    async resumeDraft() {
      if (this.resumeHandled) {
        return;
      }
      this.$store.commit(`shiftReport_setOnProcess`, true);
      try {
        await this.$store.dispatch(`shiftReport_getAvailableCache`);
        if (this.$store.state.shiftReport.availableCache.length) {
          const limits = await this.$store.dispatch(`shiftReport_getFuelLimits`);
          if (!limits.length) {
            this.$bvToast.toast('Для объекта больше не заданы лимиты топлива. Продолжить черновик пока нельзя.', {
              title: 'Черновик отчёта',
              variant: 'warning',
              solid: true
            });
            return;
          }
          this.$store.commit(`shiftReport_setFuel`, limits.map(item => ({
            id: item.id,
            name: item.name,
            unit: item.unit,
            value: null,
            note: item.lastNote || ``,
            limit: item.value
          })));
          this.$store.dispatch(`shiftReport_uploadCache`);
        }
        this.resumeHandled = true;
        if (this.$refs.stepper && typeof this.$refs.stepper.activateStep === 'function') {
          this.$refs.stepper.activateStep(1);
        }
      } catch (error) {
        this.$bvToast.toast('Не удалось автоматически открыть черновик. Вы можете продолжить отчёт вручную.', {
          title: 'Черновик отчёта',
          variant: 'warning',
          solid: true
        });
      } finally {
        this.$store.commit(`shiftReport_setOnProcess`, false);
      }
    },

    onStepperFinished() {
      this.showConfirmationModal().then(confirm => {
        if (confirm) {
          // Пользователь подтвердил отправку отчета
          this.sendReport();
        } else {
          // Пользователь выбрал редактирование - возвращаемся к предыдущему шагу
          this.goBackToLastStep();
        }
      });
    },
    
    /**
     * Возвращает пользователя к последнему шагу для редактирования
     */
    goBackToLastStep() {
      this.$store.commit('shiftReport_setOnProcess', false);
      return this.resetLastStep();
    },

    async showConfirmationModal() {
      // Получаем данные отчета
      const reportData = await this.$store.dispatch('getReportPayload');
      const selectedObject = this.$store.state.shiftReport.availableObjects.find(
        obj => obj.building_process_id === reportData.processId
      );
      
      // Сохраняем данные для доступа в модальном окне
      this.modalReportData = reportData;
      this.modalSelectedObject = selectedObject;
      
      return new Promise((resolve) => {
        this.confirmationResolve = resolve;
        this.$bvModal.show('confirmation-modal');
      });
    },
    /**
     * Экранирует HTML-символы для безопасного отображения
     * @param {string} text - Текст для экранирования
     * @returns {string} Экранированный текст
     */
    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },
    
    /**
     * Обработка подтверждения отчета
     */
    onConfirmReport() {
      if (this.onProcess) return;
      if (this.confirmationResolve) {
        this.confirmationResolve(true);
        this.confirmationResolve = null;
      } else {
        this.sendReport();
      }
    },
    
    /**
     * Обработка отмены - переход к редактированию
     */
    onEditReport() {
      this.$bvModal.hide('confirmation-modal');
      if (this.confirmationResolve) {
        this.confirmationResolve(false);
      } else {
        this.goBackToLastStep();
      }
    },
    
    /**
     * Отправляет отчет о смене и обрабатывает результат
     * Включает специальную логику для Android устройств
     */
    async sendReport() {
      this.submissionError = ''; this.submissionIssues = [];
      this.$store.commit(`shiftReport_setOnProcess`, true);
      this.$store.commit(`shiftReport_setCriticalSubmissionActive`, true);
      const isUploadByDevice = this.uploadMethod === 'uploadByDevice';
      const hasPhotos = this.readyCount > 0;

      // Держим WakeLock только до передачи отчёта серверу.
      // Дальше текст и фото отправляет backend в фоне.
      this.acquireWakeLock();

      try {
        const shift = await this.$store.dispatch(`shiftReport_sendReport`);

        this.$bvModal.hide('confirmation-modal');
        this.$store.commit(`shiftReport_setOnProcess`, false);

        const photosUploadStatus = this.photosUploadStatus;
        const photosUploadErrorMessage = this.photosUploadErrorMessage;

        let _msg;
        if (!isUploadByDevice) {
          _msg = `Отчет принят. Инструкция по отправке фотографий будет отправлена вам в Telegram.`;
        } else if (!hasPhotos) {
          _msg = `Отчет принят. Сервер отправит текст отчета и комментарий об отсутствии фото в фоне. Если возникнет сбой, вам придет сообщение в Telegram.`;
        } else if (photosUploadStatus === 'failed') {
          _msg = photosUploadErrorMessage
            || `Отчет уже создан. Не подавайте его повторно. Если в течение 4 часов фотографии не появятся в Telegram-группе, воспользуйтесь догрузкой фото в карточке отчёта.`;
        } else {
          _msg = `Отчет принят. Сервер отправит текст отчета и фотографии в фоне. Если возникнет сбой, вам придет сообщение в Telegram с инструкцией.`;
        }

        await this.$bvModal.msgBoxOk(_msg, {
          buttonSize: 'xl',
          centered: true,
          size: 'xl'
        });

        if (this.isTelegramWebApp) {
          await awaitInstallPromotionAfterSuccess({source: 'shift-report'});
        } else {
          notifyInstallPromotionSuccess({source: 'shift-report'});
        }

        // Если фотографии не загрузились, но отчет создан — открываем карточку отчёта с модалкой догрузки.
        if (isUploadByDevice && hasPhotos && photosUploadStatus === 'failed' && shift) {
          const shiftId = shift.shift_id;
          this.$store.commit(`shiftReport_setSelectedProcess`, null);
          this.clearForm();
          if (this.isTelegramWebApp) {
            closeTelegramWebApp();
          } else {
            this.$router.push({
              path: shiftId ? `/reports/${shiftId}` : '/reports',
              query: {
                openPhotoUpload: 'true'
              }
            });
          }
        } else {
          this.$store.commit(`shiftReport_setSelectedProcess`, null);
          if (this.isTelegramWebApp) {
            closeTelegramWebApp();
          } else {
            this.clearForm();
            const shiftId = shift && shift.shift_id ? shift.shift_id : null;
            this.$router.push(shiftId ? `/reports/${shiftId}` : `/reports`);
          }
        }
      } catch (err) {
        this.$store.commit(`shiftReport_setOnProcess`, false);
        this.$store.commit(`shiftReport_setUploadProgress`, { done: 0, total: 0 });

        // Если по этой смене уже есть незавершённый отчёт — показываем диалог и открываем карточку отчёта.
        if (err && err.code === 'REPORT_EXISTS') {
          this.$bvModal.hide('confirmation-modal');
          this.releaseWakeLock();
          await this.$bvModal.msgBoxOk(err.payload.msg, {
            okVariant: 'danger',
            title: 'Ошибка',            
            centered: true          
          });

          const existingShift =
            err.payload && err.payload.existingShift
              ? err.payload.existingShift
              : null;
          const shiftId = existingShift && existingShift.shift_id
            ? existingShift.shift_id
            : null;

          this.$store.commit(`shiftReport_setSelectedProcess`, null);
          this.clearForm();
          if (shiftId) {
            this.$router.push({
              path: `/reports/${shiftId}`,
              query: {
                openPhotoUpload: 'true'
              }
            });
          } else {
            this.$router.push('/reports');
          }
          return;
        }

        this.releaseWakeLock();
        this.resetLastStep();
        const failure = err && err.response && err.response.data || {};
        this.submissionError = failure.msg || err.message || 'Не удалось отправить отчёт. Введённые данные сохранены.';
        this.submissionIssues = Array.isArray(failure.issues) ? failure.issues : [];
        if (failure.status === 'REPORT_EXPLANATION_INVALID') await this.reloadSubmissionSettings();
        this.$bvModal.show('confirmation-modal');
      } finally {
        this.releaseWakeLock();
      }
    },
    
    /**
     * Очищает форму отчета о смене
     */
    clearForm() {
      this.$store.dispatch(`shiftReport_clearForm`);
    },

    /**
     * Сбрасывает последний шаг в "не завершён" — разблокирует кнопку «Готово» после ошибки отправки
     */
    resetLastStep() {
      const lastIdx = this.steps.length - 1;
      if (lastIdx < 0) return;
      this.steps[lastIdx].completed = false;
      // vue-stepper resets canContinue after finishing. Its instance is nested inside b-overlay.
      return this.$nextTick(() => {
        const stepper = this.$refs.stepper;
        if (!stepper || stepper.currentStep.index !== lastIdx) return;
        const photos = stepper.$children.find(child => child.$options.name === 'StepEight');
        if (photos) stepper.proceed({value: photos.isCanContinue()});
      });
    },

    async acquireWakeLock() {
      try {
        this.$store.dispatch('wakeLock_acquire', 'submitReport');
      } catch (e) {
        // ignore: fallback без WakeLock
      }
    },

    releaseWakeLock() {
      try {
        this.$store.dispatch('wakeLock_release', 'submitReport');
      } catch (e) {
        // ignore
      }
      try {
        this.$store.commit('shiftReport_setCriticalSubmissionActive', false);
      } catch (e) {
        // ignore
      }
    }
  },

  watch: {
    photosProcessingStatus(status) {
      if (status === 'completed' || status === 'error' || status === 'timeout') {
        this.releaseWakeLock();
        if (this.stopWatchProgress) {
          this.stopWatchProgress();
          this.stopWatchProgress = null;
        }
        if (status === 'completed') {
          this.$bvToast.toast('Фотографии успешно отправлены в Telegram-чат.', {
            title: 'Фото обработаны',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000,
            toaster: 'b-toaster-top-center'
          });
        }
      }
    }
  }
};
</script>

<style lang="scss">
.step-title {
  h4 {
    font-size: 12px;
  }
}

.upload-overlay-warning {
  max-width: 360px;
  margin: 0 auto;
  padding: 24px 16px 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  &__text-block {
    max-width: 100%;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: #343a40;
  }

  &__text {
    font-size: 13px;
    color: #6c757d;
  }

  @media (max-width: 576px) {
    padding-bottom: 96px;

    &__title {
      font-size: 14px;
    }

    &__text {
      font-size: 12px;
    }
  }
}

.spinner-border {
  width: 5rem !important;
  height: 5rem !important;
}

// Стили для модального окна подтверждения
::v-deep .modal-dialog {
  .modal-content {
    width: fit-content !important;
    max-width: none !important;
    .confirmation-report {
      .report-section {
        border-bottom: 1px solid #e9ecef;
        padding-bottom: 1rem;
        
        &:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        
        h6 {
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .work-item {
          background-color: #f8f9fa;
          padding: 0.75rem;
          border-radius: 0.375rem;
          margin-bottom: 0.5rem;
          border-left: 4px solid #007bff !important;
          
          .materials-list {
            background-color: #ffffff;
            border-radius: 0.25rem;
            padding: 0.5rem;
            border: 1px solid #e9ecef;
            margin-top: 0.5rem;
            
            .material-item {
              border-bottom: 1px solid #f1f3f4;
              
              &:last-child {
                border-bottom: none;
              }
              
              .material-name {
                font-size: 0.875rem;
                color: #495057;
              }
              
              .material-amount {
                font-size: 0.875rem;
                color: #28a745;
                font-weight: 600;
              }
            }
          }
          
          .materials-header {
            margin-top: 0.5rem;
            margin-bottom: 0.25rem;
          }
        }
        
        .fuel-item {
          background-color: #f0f8f0;
          padding: 0.5rem;
          border-radius: 0.25rem;
          border-left: 4px solid #28a745 !important;
        }
      }
      
      .border-left {
        &.border-primary {
          border-left-color: #007bff !important;
        }
        &.border-info {
          border-left-color: #17a2b8 !important;
        }
        &.border-warning {
          border-left-color: #ffc107 !important;
        }
        &.border-success {
          border-left-color: #28a745 !important;
        }
        &.border-danger {
          border-left-color: #dc3545 !important;
        }
      }
    }
  }
}

@media (max-width: 991.98px) {
  :v-deep .modal-dialog .modal-content {
    width: 100% !important;
    max-width: 100% !important;
  }
}
</style>
